import { useState, useEffect, type FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { forgotPassword, resetPassword } from '../../api/auth.api';
import { Input } from '../../components/ui/Input';
import { Button } from '../../components/ui/Button';
import { getApiErrorMessage } from '../../utils/apiError';
import toast from 'react-hot-toast';
import styles from './ForgotPasswordPage.module.css';

type Step = 'email' | 'reset';

const RESEND_COOLDOWN = 60;

export function ForgotPasswordPage() {
  const navigate = useNavigate();
  const location = useLocation();

  const [step, setStep] = useState<Step>('email');
  // С формы регистрации («почта уже занята») адрес приходит в state ссылки
  const [email, setEmail] = useState(
    () => (location.state as { email?: string } | null)?.email ?? '',
  );
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [serverError, setServerError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  // Cooldown timer
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setInterval(() => {
      setResendCooldown((prev) => prev - 1);
    }, 1000);
    return () => clearInterval(timer);
  }, [resendCooldown]);

  function validateEmail(value: string): boolean {
    const newErrors: Record<string, string> = {};
    if (!value) {
      newErrors.email = 'Введите email';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      newErrors.email = 'Некорректный формат email';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }

  function validateReset(): boolean {
    const newErrors: Record<string, string> = {};
    if (!code) {
      newErrors.code = 'Введите код';
    } else if (!/^\d{6}$/.test(code)) {
      newErrors.code = 'Код состоит из 6 цифр';
    }
    if (!newPassword) {
      newErrors.newPassword = 'Введите новый пароль';
    } else if (newPassword.length < 8) {
      newErrors.newPassword = 'Пароль должен содержать минимум 8 символов';
    } else if (new TextEncoder().encode(newPassword).length > 72) {
      // bcrypt на сервере принимает не больше 72 байт
      newErrors.newPassword =
        'Пароль слишком длинный: максимум 72 символа латиницей (русская буква считается за два)';
    }
    if (!confirmPassword) {
      newErrors.confirmPassword = 'Подтвердите пароль';
    } else if (newPassword !== confirmPassword) {
      newErrors.confirmPassword = 'Пароли не совпадают';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }

  async function handleSendCode(e: FormEvent) {
    e.preventDefault();
    setServerError('');

    const trimmedEmail = email.trim();
    if (!validateEmail(trimmedEmail)) return;

    setLoading(true);
    try {
      await forgotPassword(trimmedEmail);
      // Дальше (сброс, повторная отправка) работаем с уже очищенным адресом
      setEmail(trimmedEmail);
      toast.success('Код отправлен на ваш email');
      setResendCooldown(RESEND_COOLDOWN);
      setStep('reset');
    } catch (err: unknown) {
      setServerError(getApiErrorMessage(err, 'Не удалось отправить код. Попробуйте позже.'));
    } finally {
      setLoading(false);
    }
  }

  async function handleResetPassword(e: FormEvent) {
    e.preventDefault();
    setServerError('');

    if (!validateReset()) return;

    setLoading(true);
    try {
      await resetPassword(email, code, newPassword);
      toast.success('Пароль изменён!');
      navigate('/login', { replace: true });
    } catch (err: unknown) {
      setServerError(getApiErrorMessage(err, 'Ошибка при сбросе пароля. Попробуйте позже.'));
    } finally {
      setLoading(false);
    }
  }

  async function handleResend() {
    if (resendCooldown > 0 || resending) return;

    setResending(true);
    try {
      await forgotPassword(email);
      toast.success('Код отправлен повторно');
      setResendCooldown(RESEND_COOLDOWN);
      // Старый код больше не действует
      setCode('');
      setServerError('');
    } catch (err: unknown) {
      setServerError(getApiErrorMessage(err, 'Не удалось отправить код. Попробуйте позже.'));
    } finally {
      setResending(false);
    }
  }

  return (
    <div className={styles.container}>
      <div className={styles.wrapper}>
        <div className={styles.card}>
          {step === 'email' ? (
            <>
              <h1 className={styles.title}>Восстановление пароля</h1>
              <p className={styles.subtitle}>
                Введите email, и мы отправим код для сброса пароля
              </p>

              {serverError && (
                <div className={styles.serverError}>{serverError}</div>
              )}

              <form
                className={styles.form}
                onSubmit={handleSendCode}
                noValidate
              >
                <Input
                  label="Email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  error={errors.email}
                  autoComplete="email"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                />
                <Button type="submit" fullWidth loading={loading} size="lg">
                  Отправить код
                </Button>
              </form>
            </>
          ) : (
            <>
              <h1 className={styles.title}>Сброс пароля</h1>
              <p className={styles.subtitle}>
                Код отправлен на{' '}
                <span className={styles.emailHighlight}>{email}</span>
              </p>

              {serverError && (
                <div className={styles.serverError}>{serverError}</div>
              )}

              <form
                className={styles.form}
                onSubmit={handleResetPassword}
                noValidate
              >
                <Input
                  label="Код из письма"
                  type="text"
                  placeholder="123456"
                  value={code}
                  // Без maxLength: он обрезает вставку раньше, чем мы уберём пробелы
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  error={errors.code}
                  autoComplete="one-time-code"
                  inputMode="numeric"
                />
                <Input
                  label="Новый пароль"
                  type="password"
                  placeholder="Минимум 8 символов"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  error={errors.newPassword}
                  autoComplete="new-password"
                />
                <Input
                  label="Подтверждение пароля"
                  type="password"
                  placeholder="Повторите пароль"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  error={errors.confirmPassword}
                  autoComplete="new-password"
                />
                <Button type="submit" fullWidth loading={loading} size="lg">
                  Сбросить пароль
                </Button>
              </form>

              <div className={styles.resendRow}>
                {resendCooldown > 0 ? (
                  <span>Отправить повторно через {resendCooldown} сек.</span>
                ) : (
                  <button
                    type="button"
                    className={styles.resendLink}
                    onClick={handleResend}
                    disabled={resending}
                  >
                    Отправить код ещё раз
                  </button>
                )}
              </div>
            </>
          )}

          <p className={styles.footer}>
            <Link to="/login">Вернуться ко входу</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
